import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

import { readDscMintPriceNative } from 'lib/explorer/readDscMintPrice';

interface PriceData {
  price: number;
  change24h: number;
  marketCap: number;
  volume24h: number;
}

interface CoinPriceContextType {
  // DSC native coin price (CoinMarketCap / API — used for conversions)
  dscPrice: number;
  /** Value shown in nav / top bar: on-chain DSC_MINT_PRICE÷1e18 when available, else dscPrice */
  dscHeaderPrice: number;
  /** True when header uses DSC_MINT_PRICE from proxy (not CMC USD) */
  dscHeaderShowsMint: boolean;
  /** True until first mint read finishes or header price is still resolving */
  isDscHeaderPriceLoading: boolean;
  /** Seedx WDSC/USDT last price for header display */
  seedxPrice: number;
  /** Seedx 24h price change percent */
  seedxChangePercent: number;
  /** True until first Seedx ticker fetch finishes */
  isSeedxPriceLoading: boolean;
  dscChange24h: number;
  dscMarketCap: number;
  dscVolume24h: number;
  // USDT price from CoinMarketCap
  usdtPrice: number;
  usdtChange24h: number;
  usdtMarketCap: number;
  usdtVolume24h: number;
  // Status
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  refresh: () => void;
  // Helper functions
  formatPrice: (price: number, decimals?: number) => string;
  formatMarketCap: (value: number) => string;
  formatChange: (change: number) => string;
}

const CoinPriceContext = createContext<CoinPriceContextType>({
  dscPrice: 0,
  dscHeaderPrice: 0,
  dscHeaderShowsMint: false,
  isDscHeaderPriceLoading: true,
  seedxPrice: 0,
  seedxChangePercent: 0,
  isSeedxPriceLoading: true,
  dscChange24h: 0,
  dscMarketCap: 0,
  dscVolume24h: 0,
  usdtPrice: 1,
  usdtChange24h: 0,
  usdtMarketCap: 0,
  usdtVolume24h: 0,
  isLoading: true,
  error: null,
  lastUpdated: null,
  refresh: () => {},
  formatPrice: () => '$0.00',
  formatMarketCap: () => '$0',
  formatChange: () => '0.00%',
});

export const useCoinPrice = () => useContext(CoinPriceContext);

export const CoinPriceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ dscData, setDscData ] = useState<PriceData | null>(null);
  const [ usdtData, setUsdtData ] = useState<PriceData | null>(null);
  const [ mintNative, setMintNative ] = useState<number | null>(null);
  const [ mintFetched, setMintFetched ] = useState(false);
  const [ seedxPrice, setSeedxPrice ] = useState(0);
  const [ seedxChangePercent, setSeedxChangePercent ] = useState(0);
  const [ seedxFetched, setSeedxFetched ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ lastUpdated, setLastUpdated ] = useState<string | null>(null);

  const fetchPrices = useCallback(async() => {
    try {
      const res = await fetch('/api/cmc/price');
      if (!res.ok) {
        throw new Error('Failed to fetch prices');
      }
      const data = await res.json();

      if (data.dsc) {
        setDscData({
          price: data.dsc.price || 0,
          change24h: data.dsc.change24h || 0,
          marketCap: data.dsc.marketCap || 0,
          volume24h: data.dsc.volume24h || 0,
        });
      }

      if (data.usdt) {
        setUsdtData({
          price: data.usdt.price || 1,
          change24h: data.usdt.change24h || 0,
          marketCap: data.usdt.marketCap || 0,
          volume24h: data.usdt.volume24h || 0,
        });
      }

      setLastUpdated(data.cachedAt || new Date().toISOString());
      setError(null);
    } catch (err) {
      console.error('[CoinPrice] Failed to fetch prices:', err);
      setError('Failed to fetch price data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();

    // Refresh every 5 minutes (matches server cache TTL)
    const interval = setInterval(fetchPrices, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [ fetchPrices ]);

  const refreshMintPrice = useCallback(async() => {
    const v = await readDscMintPriceNative();
    setMintNative(v);
    setMintFetched(true);
  }, []);

  useEffect(() => {
    void refreshMintPrice();
    const mintInterval = window.setInterval(() => void refreshMintPrice(), 2 * 60 * 1000);
    return () => clearInterval(mintInterval);
  }, [ refreshMintPrice ]);

  const fetchSeedxPrice = useCallback(async() => {
    try {
      const res = await fetch('/api/seedx/ticker');
      if (!res.ok) return;
      const json = await res.json();
      if (json.success && json.data) {
        setSeedxPrice(json.data.lastPrice || 0);
        setSeedxChangePercent(json.data.priceChangePercent || 0);
      }
    } catch (err) {
      console.error('[CoinPrice] Failed to fetch Seedx price:', err);
    } finally {
      setSeedxFetched(true);
    }
  }, []);

  useEffect(() => {
    void fetchSeedxPrice();
    const seedxInterval = window.setInterval(() => void fetchSeedxPrice(), 60 * 1000);
    return () => clearInterval(seedxInterval);
  }, [ fetchSeedxPrice ]);

  const formatPrice = useCallback((price: number, decimals?: number) => {
    if (price === 0) return '$0.00';
    const dec = decimals ?? (price < 0.01 ? 6 : price < 1 ? 4 : 2);
    return `$${ price.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec }) }`;
  }, []);

  const formatMarketCap = useCallback((value: number) => {
    if (value === 0) return '$0';
    if (value >= 1_000_000_000) {
      return `$${ (value / 1_000_000_000).toFixed(2) }B`;
    }
    if (value >= 1_000_000) {
      return `$${ (value / 1_000_000).toFixed(2) }M`;
    }
    if (value >= 1_000) {
      return `$${ (value / 1_000).toFixed(2) }K`;
    }
    return `$${ value.toFixed(2) }`;
  }, []);

  const formatChange = useCallback((change: number) => {
    const prefix = change >= 0 ? '+' : '';
    return `${ prefix }${ change.toFixed(2) }%`;
  }, []);

  const dscPriceCmc = dscData?.price ?? 0;
  const dscHeaderShowsMint = mintNative != null && mintNative > 0;
  const dscHeaderPrice = dscHeaderShowsMint ? mintNative : dscPriceCmc;
  const isDscHeaderPriceLoading = !mintFetched || (isLoading && dscHeaderPrice === 0);

  const refreshAll = useCallback(async() => {
    await fetchPrices();
    await refreshMintPrice();
    await fetchSeedxPrice();
  }, [ fetchPrices, refreshMintPrice, fetchSeedxPrice ]);

  const value = useMemo(() => ({
    dscPrice: dscPriceCmc,
    dscHeaderPrice,
    dscHeaderShowsMint,
    isDscHeaderPriceLoading,
    seedxPrice,
    seedxChangePercent,
    isSeedxPriceLoading: !seedxFetched,
    dscChange24h: dscData?.change24h ?? 0,
    dscMarketCap: dscData?.marketCap ?? 0,
    dscVolume24h: dscData?.volume24h ?? 0,
    usdtPrice: usdtData?.price ?? 1,
    usdtChange24h: usdtData?.change24h ?? 0,
    usdtMarketCap: usdtData?.marketCap ?? 0,
    usdtVolume24h: usdtData?.volume24h ?? 0,
    isLoading,
    error,
    lastUpdated,
    refresh: refreshAll,
    formatPrice,
    formatMarketCap,
    formatChange,
  }), [
    dscPriceCmc,
    dscHeaderPrice,
    dscHeaderShowsMint,
    isDscHeaderPriceLoading,
    seedxPrice,
    seedxChangePercent,
    seedxFetched,
    dscData?.change24h,
    dscData?.marketCap,
    dscData?.volume24h,
    usdtData,
    isLoading,
    error,
    lastUpdated,
    refreshAll,
    formatPrice,
    formatMarketCap,
    formatChange,
  ]);

  return (
    <CoinPriceContext.Provider value={ value }>
      { children }
    </CoinPriceContext.Provider>
  );
};
